/**
 * Auth Error Types
 *
 * Typed error classes for authentication failures.
 */

/**
 * Error codes for auth operations
 */
export type AuthErrorCode =
  | 'POPUP_BLOCKED'
  | 'USER_CANCELLED'
  | 'TOKEN_EXPIRED'
  | 'TOKEN_REFRESH_FAILED'
  | 'NETWORK_ERROR'
  | 'SESSION_NOT_FOUND'
  | 'IMPERSONATION_FAILED'
  | 'INITIALIZATION_FAILED'
  | 'UNKNOWN';

/**
 * Typed auth error with error code
 */
export class AuthError extends Error {
  public readonly name = 'AuthError';

  constructor(
    message: string,
    public readonly code: AuthErrorCode,
    public readonly cause?: unknown
  ) {
    super(message);

    // Maintains proper stack trace for where error was thrown (V8 engines)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AuthError);
    }
  }

  /**
   * Check if an unknown error is an AuthError
   */
  static isAuthError(error: unknown): error is AuthError {
    return error instanceof AuthError;
  }

  /**
   * Create AuthError from an unknown error
   */
  static from(error: unknown, defaultCode: AuthErrorCode = 'UNKNOWN'): AuthError {
    if (error instanceof AuthError) {
      return error;
    }

    if (error instanceof Error) {
      // Check for common MSAL error patterns
      const message = error.message.toLowerCase();

      if (message.includes('popup') && message.includes('block')) {
        return new AuthError('Popup was blocked by the browser', 'POPUP_BLOCKED', error);
      }

      if (message.includes('user_cancelled') || message.includes('user cancelled')) {
        return new AuthError('User cancelled the operation', 'USER_CANCELLED', error);
      }

      if (message.includes('token') && message.includes('expir')) {
        return new AuthError('Token has expired', 'TOKEN_EXPIRED', error);
      }

      if (message.includes('network') || message.includes('fetch')) {
        return new AuthError('Network error during authentication', 'NETWORK_ERROR', error);
      }

      return new AuthError(error.message, defaultCode, error);
    }

    return new AuthError(String(error), defaultCode, error);
  }
}
